import { Component, OnInit, OnDestroy, ViewChild, HostListener, Input, AfterViewInit, ViewChildren, Output, EventEmitter } from '@angular/core';
import * as Chart from 'chart.js';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { getQueryParamFromAncestorRoute, getParamFromAncestorRoute } from '../../../utilities/route.utilities';
import { BillingService } from '../../../services/billing.service';

@Component({
    selector: 'billing-aged-debt-chart',
    template: '<canvas id="basechart" #basechart></canvas>',
    styles: [':host { width: 100%; position: relative; display:block; }'],
    host: { 'class': 'block-detail' }
})
export class BillingAgedDebtChartComponent implements AfterViewInit, OnDestroy {

    @ViewChildren('basechart') basechart: any;
    chart: any;
    ctx: any;
    public subscriptions: any[] = [];
    debounceTimeout = 2000;
    debounceId: any;
    chartData: any;
    heatGradientStroke: any;
    hotWaterGradientStroke: any;
    coldWaterGradientStroke: any;
    pipe: DatePipe = new DatePipe('en_GB');
    clientId: number;
    timePeriod: number = 12;
    siteId: number = 0;
    agedDebt: any;
    initalLoad: boolean = false;
    @Input() hasData: boolean = false;
    @Output() hasDataChange = new EventEmitter<boolean>();

    @Input() hasLoading: boolean = true;
    @Output() hasLoadingChange = new EventEmitter<boolean>();

    hasCurrent: boolean = false;
    has_180365Days: boolean = false;
    has_365Days: boolean = false;
    has_6090Days: boolean = false;
    has_90180Days: boolean = false;
    hasBankTransfer: boolean = false;

    constructor(
        private billingService: BillingService,
        private route: ActivatedRoute,
        private router: Router
    ) { }

    ngAfterViewInit(): void {
        if (!this.initalLoad) {
            this.subscriptions.push(this.route.queryParams.subscribe(queryParams => {
                this.onInitialise();
                this.initalLoad = true;
            }));
        }
    }

    onInitialise() {

        this.clientId = getParamFromAncestorRoute(this.route, 'clientId');
        this.siteId = getQueryParamFromAncestorRoute(this.route, 'siteId') ? getQueryParamFromAncestorRoute(this.route, 'siteId') : 0;
        this.timePeriod = getQueryParamFromAncestorRoute(this.route, 'timePeriod') == null ? 12 : getQueryParamFromAncestorRoute(this.route, 'timePeriod');

        this.search();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
        if (this.debounceId) {
            clearTimeout(this.debounceId);
        }
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: any) {
        this.createChart();
    }

    search() {
        this.chartData = [];
        this.billingService.getAgedDebt(this.clientId, this.siteId, this.timePeriod).then((result) => {
            this.agedDebt = result;
            this.chartData = this.processData(this.agedDebt);
            this.hasDataChange.emit(this.chartData);
            this.hasLoadingChange.emit(!this.chartData || this.chartData.labels.length == 0);
            this.createChart();
        }).catch(reason => {
            this.hasDataChange.emit(false);
            this.hasLoadingChange.emit(false);
        });
    }

    processData(data: any[]) {

        let monthCount: number = 0;
        this.hasCurrent = false;
        this.has_180365Days = false;
        this.has_365Days = false;
        this.has_6090Days = false;
        this.has_90180Days = false;

        let graphData = {
            labels: [],
            current: [],
            _180365Days: [],
            _365Days: [],
            _6090Days: [],
            _90180Days: []
        };

        if (data && data.length > 0) graphData.labels.push(this.pipe.transform(data[0].date, 'MMM'))

        var initializeMonth = function () {
            graphData.current.push(0);
            graphData._6090Days.push(0);
            graphData._90180Days.push(0);
            graphData._180365Days.push(0);
            graphData._365Days.push(0);
        }

        initializeMonth();

        for (let i = 0; i < data.length; i++) {

            if (data[i].current > 0) {
                graphData.current[monthCount] = data[i].current;
                this.hasCurrent = true;
            }

            if (data[i]._6090Days > 0) {
                graphData._6090Days[monthCount] = data[i]._6090Days;
                this.has_6090Days = true;
            }

            if (data[i]._90180Days > 0) {
                graphData._90180Days[monthCount] = data[i]._90180Days;
                this.has_90180Days = true;
            }

            if (data[i]._180365Days > 0) {
                graphData._180365Days[monthCount] = data[i]._180365Days;
                this.has_180365Days = true;
            }

            if (data[i]._365Days > 0) {
                graphData._365Days[monthCount] = data[i]._365Days;
                this.has_365Days = true;
            }

            if (i + 1 < data.length && data[i].date != data[i + 1].date) {
                graphData.labels.push(this.pipe.transform(data[i + 1].date, 'MMM'));
                initializeMonth();
                monthCount++;
            }
        }

        return (graphData);
    }

    private getChartConfig(): any {
        let config = {
            type: 'bar',
            options: {
                tooltips: {
                    mode: 'index',
                    enabled: true,
                    backgroundColor: '#ffffff',
                    titleFontColor: '#153954',
                    bodyFontColor: '#153954',
                    borderWidth: 2,
                    borderColor: '#009aa3',
                    callbacks: {
                        label: function (tooltipItem, data) {
                            var allData: any = data != null && data.datasets != null && tooltipItem != null && tooltipItem.datasetIndex != null ? data.datasets[tooltipItem.datasetIndex].data : [];
                            var hoverData: any = data != null && data.datasets != null && tooltipItem != null && tooltipItem.datasetIndex != null ? data.datasets[tooltipItem.datasetIndex] : [];
                            var tooltipLabel: any = data != null && data.labels != null && tooltipItem != null && tooltipItem.index != null ? data.labels[tooltipItem.index] : [];
                            var tooltipData: any = allData != null && allData.length > 0 && tooltipItem != null && tooltipItem.index != null ? allData[tooltipItem.index] : [];

                            return hoverData.label + ' £' + tooltipData;
                        }
                    }
                },
                scales: {
                    xAxes: [{
                        stacked: true,
                        barThickness: 20,
                        scaleLabel: {
                            display: true,
                            labelString: 'Month',
                            fontSize: 15,
                            fontStyle: "bold",
                        },
                        time: {
                            unit: 'month',
                            unitStepSize: 1,
                            displayFormats: {
                                'month': 'MMM'
                            }
                        },
                        gridLines: {
                            display: false
                        }
                    }],
                    yAxes: [
                        {
                            stacked: true,
                            id: "y-axis-0",
                            position: 'left',
                            scaleLabel: {
                                display: true,
                                labelString: 'Debt Value (£)',
                                fontSize: 15,
                                fontStyle: "bold",
                            },
                            gridLines: {
                                display: true
                            }
                        },
                    ]

                }
            }
        }

        return config;
    }

    createChart() {
        if (this.chart != null) this.chart.destroy();
        if (!this.basechart) return;

        let datasets = [] as any;

        if (this.hasCurrent) {
            datasets.push({
                type: 'bar',
                label: 'Current',
                yAxisID: 'y-axis-0',
                data: this.chartData.current,
                backgroundColor: '#93c01f'
            });
        }

        if (this.has_6090Days) {
            datasets.push({
                type: 'bar',
                label: '60-90 days',
                yAxisID: 'y-axis-0',
                data: this.chartData._6090Days,
                backgroundColor: '#F9E533'
            });
        }

        if (this.has_90180Days) {
            datasets.push({
                type: 'bar',
                label: '90 - 180 days',
                yAxisID: 'y-axis-0',
                data: this.chartData._90180Days,
                backgroundColor: '#FFC000'
            });
        }

        if (this.has_180365Days) {
            datasets.push({
                type: 'bar',
                label: '180-365 days',
                yAxisID: 'y-axis-0',
                data: this.chartData._180365Days,
                backgroundColor: '#DC795A'
            });
        }

        if (this.has_365Days) {
            datasets.push({
                type: 'bar',
                label: '365+ days',
                yAxisID: 'y-axis-0',
                data: this.chartData._365Days,
                backgroundColor: '#c82b2b'
            });
        }

        let config = this.getChartConfig();
        let chartConfiguration: Chart.ChartConfiguration = {
            type: config.type,
            options: config.options,
            data: {
                labels: this.chartData.labels,
                datasets: datasets,
            }
        };

        if (this.debounceId) {
            window.clearTimeout(this.debounceId);
        };

        this.debounceId = window.setTimeout(() => {
            if (this.chart != null) this.chart.destroy();
            let ctx = this.basechart.first.nativeElement;
            this.chart = new Chart(ctx, chartConfiguration);
        }, this.debounceTimeout);
    }

}
